import { makeArticle } from "./learnFactory";

export const pageAlwaysTiredDepressionFatigue = makeArticle({
  slug: "always-tired-depression-fatigue",
  title: "Why Am I So Tired All the Time? Depression Fatigue | Neuro Wellness",
  description:
    "Exhausted no matter how much you sleep? Depression fatigue is real. Learn how it differs from normal tiredness, what to rule out, and what helps.",
  canonical: "/learn/always-tired-depression-fatigue",
  h1: "Why Am I So Tired All the Time? How Depression Causes Fatigue",
  breadcrumbLabel: "Depression Fatigue",
  shortAnswer:
    "Constant tiredness that sleep doesn't fix can be a symptom of depression. Depression disrupts sleep quality, slows the brain's motivation and reward systems, and makes everyday tasks take more mental effort, so you feel drained even after a full night in bed. Fatigue is one of the most common symptoms of depression and often one of the last to improve. Because thyroid problems, anemia, sleep apnea and other conditions cause similar exhaustion, a medical check is the right first step.",
  keyTakeaways: [
    "Fatigue and low energy are among the most common symptoms of depression.",
    "Depression fatigue usually doesn't improve with rest, a weekend off or a vacation.",
    "Rule out thyroid problems, anemia, sleep apnea and medication side effects.",
    "Energy often returns as depression is treated, though it can be one of the last symptoms to lift.",
  ],
  sections: [
    {
      heading: "Not lazy, not weak: why depression drains your energy",
      paragraphs: [
        "There's tired, and then there's the kind of tired where a shower feels like a project and answering one email takes everything you have. Depression fatigue isn't a lack of willpower. Several things happen at once:",
      ],
      list: [
        "**Unrefreshing sleep.** Depression often causes trouble falling asleep, waking at 3 or 4 a.m., or sleeping too much. Even long sleep can be shallow.",
        "**A quieter reward system.** Depression dampens the brain circuits that make activities feel worth the effort, so getting started takes far more push.",
        "**Heavier mental load.** Rumination, worry and slowed thinking use up energy all day, even when you're sitting still.",
        "**Body-wide changes.** Depression is linked to stress-hormone and inflammation changes that can leave the body feeling heavy and sore.",
      ],
    },
    {
      heading: "Depression fatigue vs. normal tiredness",
      paragraphs: [
        "Normal tiredness improves with a good night's sleep, a weekend off or a vacation. Depression fatigue usually doesn't. Other signs that point toward depression:",
      ],
      list: [
        "You feel exhausted most days for two weeks or more",
        "Things you used to enjoy no longer feel worth the energy",
        "Your body feels heavy or slow",
        "It's hard to focus, decide or remember things",
        "You feel down, empty, irritable or hopeless along with the tiredness",
      ],
      paragraphsAfter: [
        "A short questionnaire such as the PHQ-9 can help a clinician see the full picture. Learn more about [[PHQ-9 and GAD-7 scores|/understanding-your-phq-9-and-gad-7-scores]].",
      ],
    },
    {
      heading: "Rule these out first",
      paragraphs: [
        "Ask your doctor about a basic workup. Common causes of ongoing fatigue include:",
      ],
      list: [
        "Thyroid problems",
        "Anemia or low iron",
        "Low vitamin B12 or vitamin D",
        "Sleep apnea (snoring, gasping at night, morning headaches)",
        "Diabetes or blood sugar problems",
        "Medication side effects, including some antidepressants, sleep aids and blood pressure medications",
        "A recent infection or long COVID",
      ],
    },
    {
      heading: "5 ways to get through the day with depression fatigue",
      paragraphs: [],
      list: [
        "**Get morning light.** Ten to fifteen minutes outside soon after waking helps reset your body clock.",
        "**Keep a fixed wake time.** Get up at the same time every day, including weekends. Sleeping in to “catch up” often makes depression fatigue worse.",
        "**Shrink the task.** Instead of “clean the kitchen,” try “wash three dishes.” Finishing something small makes the next step easier.",
        "**Limit long naps.** Keep naps under 30 minutes and before mid-afternoon.",
        "**Watch caffeine and alcohol.** Late caffeine and evening alcohol both break up sleep, which feeds next-day exhaustion.",
      ],
    },
    {
      heading: "Treating the root cause",
      paragraphs: [
        "When depression is behind the fatigue, treating the depression is what restores energy. Therapy, medication, exercise and sleep treatment all help. Fatigue is also a common leftover symptom: some people feel their mood improve on an antidepressant but stay exhausted, or find their medication makes them more tired. That's worth raising with your prescriber rather than accepting. See [[side effects you've stopped mentioning|/side-effects-youve-stopped-mentioning]].",
        "If depression has not improved after antidepressants, there are non-drug options. [[Transcranial magnetic stimulation (TMS)|/services/tms]] stimulates the brain circuits involved in mood without medication side effects such as sedation. [[SAINT®|/saint]], an accelerated, FDA-cleared form of TMS for adults with major depression that hasn't improved on antidepressants, is delivered over five days. Neuro Wellness TMS Centers of America in Coral Springs, Florida is a Verified SAINT® Provider.",
      ],
    },
    {
      heading: "What to do next",
      paragraphs: [
        "If exhaustion and low mood have lasted two weeks or more, start with a medical check, then talk with a mental health clinician about depression.",
        "Our team offers a free consultation to help you understand your options. Call 954-827-9793.",
      ],
      links: [
        { to: "/contactus", text: "Schedule a free consultation" },
        {
          to: "/am-i-a-candidate-screener",
          text: "Take the depression screening",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "Can depression make you tired all the time?",
      answer:
        "Yes. Fatigue and low energy are among the most common symptoms of depression. Depression disrupts sleep quality, motivation and mental energy, so you can feel exhausted even after plenty of sleep.",
    },
    {
      question: "Why am I still tired after 8 hours of sleep?",
      answer:
        "Depression, sleep apnea, thyroid problems, anemia and medication side effects can all make sleep unrefreshing. If you wake up tired most days, talk to a doctor about both your sleep and your mood.",
    },
    {
      question: "How do I know if my fatigue is depression?",
      answer:
        "Depression fatigue usually comes with other signs lasting two weeks or more, such as low mood, loss of interest, trouble concentrating, sleep or appetite changes, or hopelessness. A clinician can screen for depression with a short questionnaire like the PHQ-9.",
    },
    {
      question: "Can antidepressants make fatigue worse?",
      answer:
        "Some can, especially early on or at certain doses. Don't stop a medication on your own; tell your prescriber about the fatigue so they can adjust your plan.",
    },
    {
      question: "Does depression fatigue go away?",
      answer:
        "Usually, yes, with treatment, though energy can be one of the last symptoms to return. Tracking your energy alongside your mood helps you and your clinician see progress.",
    },
  ],
  sources: [
    {
      citation: "National Institute of Mental Health. Depression.",
      url: "https://www.nimh.nih.gov/health/publications/depression",
    },
    {
      citation:
        "Simon GE, VonKorff M, Piccinelli M, et al. An international study of the relation between somatic symptoms and depression. New England Journal of Medicine, 1999.",
      url: "https://pubmed.ncbi.nlm.nih.gov/10536124/",
    },
  ],
  relatedLinks: [
    {
      title: "Depression, anxiety and insomnia",
      href: "/depression-anxiety-insomnia",
      description: "When sleep, mood and worry feed each other.",
    },
    {
      title: "High-Functioning Depression Symptoms: 10 Quiet Signs",
      href: "/high-functioning-depression-symptoms-treatment",
      description: "Everyday habits that can be quiet signs of depression.",
    },
    {
      title: "Can depression cause physical pain?",
      href: "/learn/can-depression-cause-physical-pain",
      description: "Why depression hurts and what helps.",
    },
    {
      title: "Can't stop crying for no reason?",
      href: "/learn/cant-stop-crying-for-no-reason",
      description: "What frequent crying can mean and when to get help.",
    },
    {
      title: "What is SAINT® TMS?",
      href: "/learn/what-is-saint-tms",
      description:
        "The five-day, FDA-cleared option when antidepressants haven't worked.",
    },
  ],
});
